---
title: Add a workflow to a persona
summary: Install a multi-step workflow template, choose its channel, and publish it to a persona.
visibility: authenticated
feature: workflow_engine
audience: author-admin
section: Advanced authoring
order: 98
last_verified: 2026-10-28
owner: docs
---

# Add a workflow to a persona

A **workflow** is a guided, multi-step process a persona can run end-to-end — for example,
collecting an order and sending a payment link. You attach workflows on the **Workflows** card
in the persona's [Workshop](/help/advanced/use-the-workshop) (Advanced mode).

## Install a workflow

Workflows are **predefined templates** — you install one rather than drawing it. (There's no
visual workflow designer; templates are authored separately as definitions and made available
in the catalog.)

1. On the **Workflows** card, click **+ Add workflow**.
2. Pick a workflow from the catalog. Each shows its name, version, description, and the
   languages it supports.
3. On the installed row, set the **Channel** it should start on: *any channel, including web
   chat*, `whatsapp`, `chat` (web chat), or `voice`. A workflow on any channel or on `chat`
   answers every web-chat message in place of the persona's normal answer, with no sources,
   and the Workshop warns about it.
4. Click **Save** to publish the binding to the persona.

## What's inside a workflow

You don't edit the steps here, but it helps to know the shape (see the
[mental model](/help/concepts/personas-bundles-workflows)):

- A workflow is a graph of **nodes** — the persona talks to the customer (`agent_turn`), calls
  a tool (`tool_call`), branches, waits for an event (like a payment confirmation), or ends.
- It collects values into **slots** (a phone number, an order quantity) as it runs.
- Steps where the persona talks can carry **guardrails** — plain-language rules for that step.

## Bind the capabilities it needs

A workflow declares the capabilities its `tool_call` steps require. If any aren't bound on the
**Tools** card, the Review tab's wiring subsection flags *"Workflow … needs … but it is not
bound"* with a jump to Tools. Bind those [tools](/help/advanced/add-tools) before relying on
the workflow.

## Trying it out

If your deployment includes the guided Wizard, use the Preview rail (right-hand panel) to
sanity-check the persona's replies. A deeper,
step-by-step workflow test runner is available on deployments that enable the workflow design
tools; if you see a **Test** button on a workflow row, it opens a sandboxed run where external
effects (messages, charges) are stubbed.

## See also

- [Add tools](/help/advanced/add-tools) — bind the capabilities a workflow calls.
- [Advanced authoring in the Workshop](/help/advanced/use-the-workshop) — Save, Review, and Preview.
